import { Component, PureComponent } from "react";

import { cn } from "@btnet/utilities-core";

import { BdsSelect } from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect, type BTSelectProps } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { PercentageDisplay } from "commonComponents/financial/PercentageDisplay/PercentageDisplay";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";
import { SplitRowWithDotLeader } from "commonComponents/utilities/SplitRowWithDotLeader/SplitRowWithDotLeader";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { AccountingTaxRateConnected } from "entity/tax/common/AccountingTaxRateConnected/AccountingTaxRateConnected";
import { ImportOptionId, NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

import "./TaxRateSelect.less";

class TaxGroupSelectItemInternal extends Component<BTSelectItem<TaxGroupServiceListItemExtraData>> {
    render() {
        const { title } = this.props;
        const {
            totalEffectiveRate,
            taxRates,
            isActive,
            accountingType,
            isOriginatedFromAccounting,
        } = this.props.extraData!;
        const leftText = `${title}${!isActive ? " (Inactive)" : ""}`;
        const left = (
            <BTRow gutter={5}>
                <BTCol>{leftText}</BTCol>
                {accountingType && isOriginatedFromAccounting && (
                    <AccountingTaxRateConnected accountingType={accountingType} />
                )}
            </BTRow>
        );
        return (
            <div className="TaxGroupContainer">
                <SplitRowWithDotLeader
                    left={left}
                    right={
                        <PercentageDisplay
                            data-testid="totalEffectiveRate"
                            value={totalEffectiveRate}
                            decimalScale={5}
                            fixedDecimalScale={false}
                        />
                    }
                    leaderType="dashed"
                />
                {taxRates.map((tr) => {
                    return (
                        <div key={tr.taxRateId} className="TaxRateInGroup">
                            <TextTruncate>
                                <span>
                                    {tr.taxRateName}
                                    {!tr.isActive && " (Inactive)"}
                                </span>
                            </TextTruncate>
                            <PercentageDisplay
                                data-testid="taxRatePercent"
                                value={tr.taxRatePercent}
                                decimalScale={5}
                                fixedDecimalScale={false}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

class TaxRateSelectItemInternal extends Component<BTSelectItem<TaxGroupServiceListItemExtraData>> {
    render() {
        const { title } = this.props;
        const { totalEffectiveRate, isActive, accountingType, isOriginatedFromAccounting } =
            this.props.extraData!;
        const leftText = `${title}${!isActive ? " (Inactive)" : ""}`;
        const left = (
            <BTRow gutter={5}>
                <BTCol>{leftText}</BTCol>
                {accountingType && isOriginatedFromAccounting && (
                    <AccountingTaxRateConnected accountingType={accountingType} />
                )}
            </BTRow>
        );
        return (
            <SplitRowWithDotLeader
                left={left}
                right={
                    <PercentageDisplay
                        data-testid="totalEffectiveRate"
                        value={totalEffectiveRate}
                        decimalScale={5}
                        fixedDecimalScale={false}
                    />
                }
                leaderType="dashed"
            />
        );
    }
}

export interface ITaxRateSelectProps<FormValues>
    extends BTSelectProps<FormValues, TaxGroupServiceListItemExtraData> {
    useBds?: boolean;
}

export class TaxRateSelect<FormValues = undefined> extends PureComponent<
    ITaxRateSelectProps<FormValues>
> {
    render() {
        const { useBds, ...selectProps } = this.props;
        const SelectComponent = useBds ? BdsSelect : BTSelect;

        if (
            this.props.readOnly &&
            this.props.value !== NoTaxId &&
            this.props.value !== ImportOptionId
        ) {
            const item = this.props.treeData?.find((item) => item.id === this.props.value);
            if (item)
                return (
                    <div
                        className={cn("tax-rate-select-container", this.props.className)}
                        data-testid={this.props["data-testid"]}
                    >
                        <TaxRateSelectItemInternal {...item} />
                    </div>
                );
        }

        return (
            <SelectComponent<FormValues, TaxGroupServiceListItemExtraData>
                {...selectProps}
                treeData={this.props.treeData}
                className={cn("tax-rate-select-container", this.props.className)}
                dropdownClassName="TaxRateSelect"
                dropdownCanExpand
                customItemRender={(item: BTSelectItem<TaxGroupServiceListItemExtraData>) => {
                    if (parseInt(item.id) === NoTaxId || parseInt(item.id) === ImportOptionId) {
                        return item.title;
                    }
                    // extraData is typed as required but mock select items occasionally
                    // omit it; fall back to the group renderer (which handles the rest).
                    if (item.extraData?.isAutoGenerated) {
                        return <TaxRateSelectItemInternal {...item} />;
                    }
                    return <TaxGroupSelectItemInternal {...item} />;
                }}
                filterOn={(item: BTSelectItem<TaxGroupServiceListItemExtraData>) => {
                    const rateNames = item.extraData?.taxRates?.map((tr) => tr.taxRateName) ?? [];
                    return [item.title, ...rateNames].join("|");
                }}
            />
        );
    }
}
